import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { FilterableSelect } from '../ui/FilterableSelect';
import { fetchRegions } from '../../features/geo/geoThunks';
import {
  buildRegionOptions,
  buildCommuneOptions,
  buildSettlementOptions,
  findCommuneById,
  resolvePlaceValue,
} from '../../features/geo/geoUtils';
import { COUNTRY_ISO2 } from '../../features/geo/geoConstants';
import { BLANK_ADDRESS, validateAddress } from '../../features/geo/addressUtils';

/**
 * Re-exported so the component stays the single import for anything working
 * with an address, while the shape and the rules themselves live in the geo
 * feature and can be reached without rendering anything.
 *
 * @typedef {import('../../features/geo/addressUtils').AddressValue} AddressValue
 */
export { BLANK_ADDRESS, validateAddress };

const inputClass =
  'w-full bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl px-4 py-2.5 text-sm text-ninja-dark dark:text-white outline-none focus:border-ninja-green transition-colors disabled:opacity-60';

const labelClass = 'block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5';

const Field = ({ label, error, children }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
    {error && <p className="text-danger text-xs mt-1">{error}</p>}
  </div>
);

/**
 * The address block of an order, shared by the creation form and the editor.
 *
 * Both build the same address from the same official catalog, so they are one
 * component: they had been two copies and had already drifted apart in the
 * validation of the colonia, in every placeholder and in the disabled state of
 * the region select. Any change to the copy, the cascade or the validation
 * belongs here, never in a consumer.
 *
 * It owns the cascade: choosing a region clears the commune, the colonia and
 * the postal code, and choosing a commune clears the last two. A commune from
 * the previous region would quietly ship the parcel somewhere else.
 *
 * Lives outside `components/ui` on purpose — it reads `state.geo`, and that
 * folder is for primitives that do not know about features.
 *
 * @param {object} props
 * @param {string} props.country - ISO2 of the account the order belongs to.
 * @param {AddressValue} props.value
 * @param {(next: Partial<AddressValue>) => void} props.onChange - Receives only
 *   the fields that changed, cascade included.
 * @param {Object.<string, string>} [props.errors]
 */
export const AddressFields = ({ country, value, onChange, errors = {} }) => {
  const dispatch = useDispatch();
  const isMexico = country === COUNTRY_ISO2.MX;

  const { regionsByCountry, loadingByCountry } = useSelector((state) => state.geo);
  const regions = useMemo(() => regionsByCountry[country] || [], [regionsByCountry, country]);
  const loadingRegions = Boolean(loadingByCountry[country]);

  useEffect(() => {
    if (country) dispatch(fetchRegions(country));
  }, [country, dispatch]);

  const regionOptions = useMemo(() => buildRegionOptions(regions), [regions]);
  const communeOptions = useMemo(
    () => buildCommuneOptions(regions, value.region),
    [regions, value.region]
  );
  const settlementOptions = useMemo(
    () => (isMexico ? buildSettlementOptions(regions, value.region, value.commune) : []),
    [isMexico, regions, value.region, value.commune]
  );

  /**
   * Rewrites a loaded address to the values the catalog keys its options by.
   *
   * Stored addresses hold whichever of the two fields the backend happened to
   * save — an order carries `"La Florida"` where the options are keyed by
   * `"la_florida"` — and a select whose value matches no option renders empty
   * over data that is really there. In Mexico it also emptied the colonia list,
   * because the settlements are filtered by the commune's value.
   *
   * It runs here and not when a consumer seeds its form because the catalog has
   * not arrived yet at that point. The parent's state is corrected too, not
   * just what is painted, so saving sends the canonical value — the same one
   * the creation form sends.
   */
  useEffect(() => {
    if (regionOptions.length === 0) return;

    const patch = {};

    const region = resolvePlaceValue(regionOptions, value.region);
    if (region !== value.region) patch.region = region;

    // The commune options hang off the region, so they are rebuilt from the
    // resolved one rather than from what the parent still holds.
    const communes = buildCommuneOptions(regions, region);
    const commune = resolvePlaceValue(communes, value.commune);
    if (commune !== value.commune) patch.commune = commune;

    // No cascade clearing here: this is a resolution, not an operator choosing
    // a different place. An empty patch emits nothing, which is what keeps this
    // from looping over a consumer's inline `onChange`.
    if (Object.keys(patch).length > 0) onChange(patch);
  }, [regionOptions, regions, value.region, value.commune, onChange]);

  const handleRegionChange = (region) => {
    onChange({ region, commune: '', settlementId: '', settlement: '', postalCode: '' });
  };

  const handleCommuneChange = (commune) => {
    onChange({ commune, settlementId: '', settlement: '', postalCode: '' });
  };

  /**
   * The chosen row carries both the settlement name and its postal code, so the
   * two can no longer disagree with each other or with the commune.
   */
  const handleSettlementChange = (settlementId) => {
    const row = findCommuneById(regions, settlementId);
    onChange({
      settlementId,
      settlement: row?.settlement || '',
      postalCode: row?.postal_code || '',
    });
  };

  return (
    <>
      <Field label="Calle" error={errors.street}>
        <input
          name="street"
          value={value.street}
          onChange={(e) => onChange({ street: e.target.value })}
          className={inputClass}
        />
      </Field>

      <Field label="Número" error={errors.streetNumber}>
        <input
          name="streetNumber"
          value={value.streetNumber}
          onChange={(e) => onChange({ streetNumber: e.target.value })}
          className={inputClass}
        />
      </Field>

      <div>
        <FilterableSelect
          variant="form"
          label={isMexico ? 'Estado' : 'Región'}
          value={value.region}
          onChange={handleRegionChange}
          options={regionOptions}
          disabled={loadingRegions && regionOptions.length === 0}
          hasError={Boolean(errors.region)}
          placeholder={loadingRegions ? 'Cargando...' : 'Seleccionar...'}
          searchPlaceholder={isMexico ? 'Buscar estado...' : 'Buscar región...'}
        />
        {errors.region && <p className="text-danger text-xs mt-1">{errors.region}</p>}
      </div>

      <div>
        <FilterableSelect
          variant="form"
          label={isMexico ? 'Municipio' : 'Comuna'}
          value={value.commune}
          onChange={handleCommuneChange}
          options={communeOptions}
          disabled={!value.region}
          hasError={Boolean(errors.commune)}
          placeholder={
            value.region
              ? 'Seleccionar...'
              : `Primero selecciona ${isMexico ? 'un estado' : 'una región'}`
          }
          searchPlaceholder={isMexico ? 'Buscar municipio...' : 'Buscar comuna...'}
        />
        {errors.commune && <p className="text-danger text-xs mt-1">{errors.commune}</p>}
      </div>

      {isMexico && (
        <>
          <div>
            <FilterableSelect
              variant="form"
              label="Colonia"
              value={value.settlementId}
              onChange={handleSettlementChange}
              options={settlementOptions}
              disabled={!value.commune}
              hasError={Boolean(errors.settlementId)}
              placeholder={
                value.commune
                  ? value.settlement || 'Seleccionar...'
                  : 'Primero selecciona un municipio'
              }
              searchPlaceholder="Buscar colonia o código postal..."
            />
            {errors.settlementId && (
              <p className="text-danger text-xs mt-1">{errors.settlementId}</p>
            )}
          </div>

          <Field label="Código Postal">
            {/* Derived from the colonia — never typed, so the two cannot disagree. */}
            <input
              value={value.postalCode}
              readOnly
              placeholder="Se completa con la colonia"
              className={clsx(inputClass, 'cursor-not-allowed text-text-secondary')}
            />
          </Field>
        </>
      )}
    </>
  );
};
